import { useState } from 'react';
import { api } from '../services/api';
import { initMetaPixel } from '../utils/metaPixel';
import { initGoogleAnalytics } from '../utils/googleAnalytics';
import './CookieBanner.css';

const COOKIE_VERSION = 'v1.0';
const STORAGE_KEY = 'cookie_consent';

interface CookiePrefs {
  version: string;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

function getSaved(): CookiePrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePrefs;
    // Ak sa zmenila verzia, zobraziť banner znova
    if (parsed.version !== COOKIE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

async function logConsent(prefs: Omit<CookiePrefs, 'version'>) {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    await api.post('/consent/cookies', {
      user_id: user?.id || null,
      necessary: prefs.necessary,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
    });
  } catch {
    // Tichá chyba — logovanie nie je kritické
  }
}

export function CookieBanner() {
  // Lazy init — banner sa zobrazí iba ak ešte nebol uložený consent
  const [show, setShow] = useState(() => !getSaved());
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const save = (prefs: Omit<CookiePrefs, 'version'>) => {
    const full: CookiePrefs = { version: COOKIE_VERSION, ...prefs };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
    logConsent(prefs);
    // Aktivuj nástroje hneď podľa udeleného súhlasu (bez čakania na reload).
    // Každý init si consent overí sám a načíta script len ak je povolený.
    if (prefs.analytics) initGoogleAnalytics();
    if (prefs.marketing) initMetaPixel();
    setShow(false);
    setShowSettings(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const acceptNecessary = () => save({ necessary: true, analytics: false, marketing: false });
  const saveSettings = () => save({ necessary: true, analytics, marketing });

  if (!show) return null;

  return (
    <>
      {/* Hlavný banner */}
      {!showSettings && (
        <div className="cookie-banner">
          <div className="cookie-banner-text">
            <strong>Táto stránka používa cookies</strong>
            <p>Nevyhnutné cookies zabezpečujú základné funkcie. So súhlasom použijeme aj analytické a marketingové cookies na zlepšenie tvojho zážitku.</p>
          </div>
          <div className="cookie-banner-actions">
            <button className="cookie-btn cookie-btn--ghost" onClick={() => setShowSettings(true)}>Nastavenia</button>
            <button className="cookie-btn cookie-btn--secondary" onClick={acceptNecessary}>Len nevyhnutné</button>
            <button className="cookie-btn cookie-btn--primary" onClick={acceptAll}>Prijať všetky</button>
          </div>
        </div>
      )}

      {/* Nastavenia panel */}
      {showSettings && (
        <div className="cookie-settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="cookie-settings-modal" onClick={e => e.stopPropagation()}>
            <h3 className="cookie-settings-title">Nastavenia cookies</h3>

            <div className="cookie-settings-row">
              <div className="cookie-settings-info">
                <div className="cookie-settings-label">Nevyhnutné cookies</div>
                <div className="cookie-settings-desc">Prihlásenie, bezpečnosť, základné funkcie aplikácie. Nedajú sa vypnúť.</div>
              </div>
              <div className="cookie-settings-toggle cookie-settings-toggle--on">Vždy</div>
            </div>

            <div className="cookie-settings-row">
              <div className="cookie-settings-info">
                <div className="cookie-settings-label">Analytické cookies</div>
                <div className="cookie-settings-desc">Pomáhajú nám pochopiť, ako používateľov používajú aplikáciu (napr. Google Analytics).</div>
              </div>
              <label className="settings-toggle">
                <input type="checkbox" checked={analytics} onChange={e => setAnalytics(e.target.checked)} />
                <span className="settings-toggle-slider" />
              </label>
            </div>

            <div className="cookie-settings-row">
              <div className="cookie-settings-info">
                <div className="cookie-settings-label">Marketingové cookies</div>
                <div className="cookie-settings-desc">Umožňujú zobrazovanie relevantnej reklamy na iných platformách (napr. Meta Pixel).</div>
              </div>
              <label className="settings-toggle">
                <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} />
                <span className="settings-toggle-slider" />
              </label>
            </div>

            <div className="cookie-settings-btns">
              <button className="cookie-btn cookie-btn--ghost" onClick={() => setShowSettings(false)}>Späť</button>
              <button className="cookie-btn cookie-btn--primary" onClick={saveSettings}>Uložiť nastavenia</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
